/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * KoboToolbox Primary API
 * This page documents all KoboToolbox API endpoints, except for those implementing the OpenRosa protocol, which are [documented separately](/api/openrosa/docs/).

The endpoints are grouped by area of intended use. Each category contains related endpoints, with detailed documentation on usage and configuration. Use this as a reference to quickly find the right endpoint for managing projects, forms, data, permissions, integrations, logs, and organizational resources.

**General note**: All projects (whether deployed or draft), as well as all library content (questions, blocks, templates, and collections) in the user-facing application are represented in the API as "assets".
 * OpenAPI spec version: 2.0.0 (api_v2)
 */
import { useQuery } from '@tanstack/react-query'
import type { QueryFunction, QueryKey, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'

import type { EnvironmentResponse } from '../../models/environmentResponse'

import type { ErrorDetail } from '../../models/errorDetail'

import type { SocialAppDetail } from '../../models/socialAppDetail'

import { fetchWithAuth } from '../../orval.mutator'

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1]

/**
 * Retrieve Kobo server environment configurations.

This endpoint provides a wide range of configuration details required by the frontend client, including:
- Legal, support, and community URLs (e.g. Terms of Service, Privacy Policy).
- Frontend configuration (retry times, usage limit enforcement, etc).
- Global static lists (sectors, countries, interface languages, operational purposes).
- Metadata configuration (available project and user metadata fields).
- Features configurations (MFA, password entropy meter, custom guidance text).
- Sign-in and account creation branding (theme, logos, background and supporting image, supporting text) and behaviour (whether registration is open, whether a username is accepted at sign-in).
- Additional integrations and features (Stripe public key, ASR & MT enablement, Social apps).
- Pagination behaviour (row limit past which list counts are capped).

 */
export type environmentRetrieveResponse200 = {
  data: EnvironmentResponse
  status: 200
}

export type environmentRetrieveResponseSuccess = environmentRetrieveResponse200 & {
  headers: Headers
}

export type environmentRetrieveResponse = environmentRetrieveResponseSuccess

export const getEnvironmentRetrieveUrl = () => {
  return `/api/v2/environment/`
}

export const environmentRetrieve = async (options?: RequestInit): Promise<environmentRetrieveResponse> => {
  return fetchWithAuth<environmentRetrieveResponse>(getEnvironmentRetrieveUrl(), {
    ...options,
    method: 'GET',
  })
}

export const getEnvironmentRetrieveQueryKey = () => {
  return ['api', 'v2', 'environment'] as const
}

export const getEnvironmentRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof environmentRetrieve>>,
  TError = unknown,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof environmentRetrieve>>, TError, TData>
  request?: SecondParameter<typeof fetchWithAuth>
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getEnvironmentRetrieveQueryKey()

  const queryFn: QueryFunction<Awaited<ReturnType<typeof environmentRetrieve>>> = ({ signal }) =>
    environmentRetrieve({ signal, ...requestOptions })

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof environmentRetrieve>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type EnvironmentRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof environmentRetrieve>>>
export type EnvironmentRetrieveQueryError = unknown

export function useEnvironmentRetrieve<
  TData = Awaited<ReturnType<typeof environmentRetrieve>>,
  TError = unknown,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof environmentRetrieve>>, TError, TData>
  request?: SecondParameter<typeof fetchWithAuth>
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getEnvironmentRetrieveQueryOptions(options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * ## Retrieve a single sign-on provider

Resolves the `provider_id` used in single sign-on URLs into the provider's display
name, so a client can render the "Log in with …" screen and tell a real provider
from a typo.

Returns display data only, never `client_id`, secrets, or the provider's server URL.

Examples:
```shell
  curl -X GET https://kf.kobotoolbox.org/api/v2/social-apps/nca/
```

> Response 200
```json
{
    "provider_id": "nca",
    "name": "Norwegian Church Aid"
}
```

Requires no authentication, and resolves providers that are hidden from the login
page as well as those shown on it: a hidden provider is not advertised, but is
usable by anyone holding its link, and that link already contains the
`provider_id`. There is intentionally no endpoint listing all providers, the
`social_apps` property of `/api/v2/environment/` lists the public ones only.

An unknown `provider_id` returns `404`.

 */
export type socialAppsRetrieveResponse200 = {
  data: SocialAppDetail
  status: 200
}

export type socialAppsRetrieveResponse404 = {
  data: ErrorDetail
  status: 404
}

export type socialAppsRetrieveResponseSuccess = socialAppsRetrieveResponse200 & {
  headers: Headers
}
export type socialAppsRetrieveResponseError = socialAppsRetrieveResponse404 & {
  headers: Headers
}

export type socialAppsRetrieveResponse = socialAppsRetrieveResponseSuccess | socialAppsRetrieveResponseError

export const getSocialAppsRetrieveUrl = (providerId: string) => {
  return `/api/v2/social-apps/${providerId}/`
}

export const socialAppsRetrieve = async (
  providerId: string,
  options?: RequestInit,
): Promise<socialAppsRetrieveResponse> => {
  return fetchWithAuth<socialAppsRetrieveResponse>(getSocialAppsRetrieveUrl(providerId), {
    ...options,
    method: 'GET',
  })
}

export const getSocialAppsRetrieveQueryKey = (providerId?: string) => {
  return ['api', 'v2', 'social-apps', providerId] as const
}

export const getSocialAppsRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof socialAppsRetrieve>>,
  TError = ErrorDetail,
>(
  providerId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof socialAppsRetrieve>>, TError, TData>
    request?: SecondParameter<typeof fetchWithAuth>
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getSocialAppsRetrieveQueryKey(providerId)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof socialAppsRetrieve>>> = ({ signal }) =>
    socialAppsRetrieve(providerId, { signal, ...requestOptions })

  return { queryKey, queryFn, enabled: !!providerId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof socialAppsRetrieve>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type SocialAppsRetrieveQueryResult = NonNullable<Awaited<ReturnType<typeof socialAppsRetrieve>>>
export type SocialAppsRetrieveQueryError = ErrorDetail

export function useSocialAppsRetrieve<TData = Awaited<ReturnType<typeof socialAppsRetrieve>>, TError = ErrorDetail>(
  providerId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof socialAppsRetrieve>>, TError, TData>
    request?: SecondParameter<typeof fetchWithAuth>
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getSocialAppsRetrieveQueryOptions(providerId, options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}
